/**
 * BOSS Aylık Maaş Sayfası — Faz 5.5: Maaşa ekleme/kesinti butonları eklendi.
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import {
  Coins, ChevronLeft, ChevronRight, CheckCircle2,
  Banknote, Building, GitMerge, X, Users, Activity, AlertCircle, Plus, Minus, Download,
  Lock, Unlock, Trash2, Filter, ChevronDown, ChevronUp,
} from 'lucide-react'
import {
  tumAktifPersonellerinMaliyeti,
  aktifMesaiMaliyeti,
  type ToplamMaliyetOzeti,
  type AktifMaliyetOzeti,
} from '@/lib/maas/toplam-maliyet'
import type { MaasHesabi } from '@/lib/maas/maas-hesap'
import { IZIN_GUN_SAAT_KARSILIGI, AYLIK_STANDART_SAAT } from '@/lib/maas/sabitler'
import {
  donemOdemelerini, odemeIptalEt, type MaasOdemesi,
} from '@/lib/services/maas-odeme-service'
import {
  maasHareketiSil, type MaasHareketi, type MaasHareketTipi,
} from '@/lib/services/maas-hareket-service'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import { tumPozisyonlariGetir } from '@/lib/services/pozisyon-service'
import { ayTumPuantajlar, aktifPuantajlar, type Puantaj } from '@/lib/services/puantaj-service'
import { maaslariCsvOlustur, csvIndir } from '@/lib/maas/excel-export'
import {
  ayDurumu, ayKapat, ayAc, type AyKapanisi,
} from '@/lib/services/ay-kapanisi-service'
import type { Sube, Pozisyon } from '@/types'
import { useAuth } from '@/contexts/AuthContext'
import { useYetki } from '@/hooks/useYetki'
import { YETKILER } from '@/lib/yetkiler'
import { OdemeModal } from '@/components/maas/OdemeModal'
import { MaasHareketEkleModal } from '@/components/maas/MaasHareketEkleModal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import type { KullaniciPersonel } from '@/types'

const TOLERANS = 1

/** Build-up: tam ayda hak edilen aylık izin (IZIN_HAKKI_MAX). Tam ay = (STD_SAAT/10 − 4) gün çalışma + 4 gün izin. */
const TAM_AY_IZIN_GUN = 4

function buAyStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function ayEtiketi(ay: string): string {
  const [y, m] = ay.split('-').map(Number)
  if (!y || !m) return ay
  const aylar = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık']
  return `${aylar[m - 1]} ${y}`
}

function paraTam(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function paraKurus(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

interface MaasKalem { etiket: string; tutar: number; alt?: string }

/** Maaş netini oluşturan kalemleri (toplamı netMaas'a eşit) bordro biçiminde döndürür. */
function maasKalemleri(h: MaasHesabi, p: KullaniciPersonel): MaasKalem[] {
  const k: MaasKalem[] = []
  const s = h.saatlikKarsilik

  if (h.buildUp) {
    // Tam ay = çalışma payı (26 gün) + aylık izin (4 gün). İzin ayrı kalem gösterilir.
    const izinSaatStd = TAM_AY_IZIN_GUN * IZIN_GUN_SAAT_KARSILIGI          // 40 sa
    const calismaPayi = p.ucret - izinSaatStd * s                          // 45.706
    const stdCalismaSaat = AYLIK_STANDART_SAAT - izinSaatStd               // 260 sa
    const calismaGun = Math.round(stdCalismaSaat / IZIN_GUN_SAAT_KARSILIGI) // 26 gün
    k.push({
      etiket: 'Aylık maaş — çalışma payı',
      tutar: calismaPayi,
      alt: `${calismaGun} gün × ${IZIN_GUN_SAAT_KARSILIGI} sa (${stdCalismaSaat} sa) · sözleşme ${paraTam(p.ucret)} ₺`,
    })

    // Hak edilen aylık izin (build-up: kullanılsa da kullanılmasa da ödenir → izin yapmadıysa alacak)
    if (h.izinAlacagi > 0) {
      k.push({
        etiket: `Hak edilen izin (${h.izinHakki} gün × ${IZIN_GUN_SAAT_KARSILIGI} sa)`,
        tutar: h.izinAlacagi,
        alt: `${h.izinHakki * IZIN_GUN_SAAT_KARSILIGI} sa × ${paraTam(s)} ₺ · izin yapılmadıysa alacak`,
      })
    }

    // Fazla/eksik çalışma = gerçek çalışılan saat − 26 gün payı (izin buna DAHİL DEĞİL)
    const fazlaTutar = h.bazUcret - calismaPayi
    if (Math.abs(fazlaTutar) >= 1) {
      const fazlaSaat = s > 0 ? fazlaTutar / s : 0
      const fazla = fazlaTutar > 0
      k.push({
        etiket: fazla ? 'Fazla çalışma' : 'Eksik çalışma',
        tutar: fazlaTutar,
        alt: `${Math.abs(fazlaSaat).toFixed(1)} saat ${fazla ? 'fazla' : 'eksik'} × ${paraTam(s)} ₺ · (çalışılan ${h.toplamSaat.toFixed(1)} sa − ${stdCalismaSaat} sa)`,
      })
    }

    if (h.yillikIzinOdemesi > 0)
      k.push({ etiket: `Yıllık izin kullanımı (${h.yillikIzinGun} gün)`, tutar: h.yillikIzinOdemesi })
  } else if (p.maasTipi === 'saatlik') {
    k.push({ etiket: 'Çalışılan saat karşılığı', tutar: h.bazUcret, alt: `${h.toplamSaat.toFixed(1)} saat × ${paraTam(s)} ₺` })
    if (h.yillikIzinOdemesi > 0)
      k.push({ etiket: `Yıllık izin (${h.yillikIzinGun} gün)`, tutar: h.yillikIzinOdemesi })
  } else {
    // Ofis aylık (sabit)
    k.push({ etiket: 'Aylık maaş (sabit)', tutar: h.bazUcret })
    if (h.eksikMesaiKesinti > 0)
      k.push({ etiket: 'Eksik mesai kesintisi', tutar: -h.eksikMesaiKesinti })
    if (h.izinMaasKesinti > 0)
      k.push({ etiket: 'İzin maaş kesintisi', tutar: -h.izinMaasKesinti })
    if (h.yillikIzinOdemesi > 0)
      k.push({ etiket: `Yıllık izin (${h.yillikIzinGun} gün)`, tutar: h.yillikIzinOdemesi })
  }

  // Ortak kalemler
  if (h.tatilBonusToplam > 0)
    k.push({ etiket: 'Resmi tatil bonusu', tutar: h.tatilBonusToplam })
  if (h.dogumGunuBonus > 0)
    k.push({ etiket: 'Doğum günü bonusu', tutar: h.dogumGunuBonus })
  if (h.puantajToplam !== 0)
    k.push({ etiket: 'Puantaj (ödül/ceza)', tutar: h.puantajToplam })
  if (h.molaAsanKesinti > 0)
    k.push({ etiket: 'Mola aşımı kesintisi', tutar: -h.molaAsanKesinti })
  if (h.gecGelmeCezasi > 0)
    k.push({ etiket: 'Geç gelme cezası', tutar: -h.gecGelmeCezasi, alt: `${h.gecGelmeSayisi} geç · ilk 2 cezasız` })
  if (h.noShowCezasi > 0)
    k.push({ etiket: `Gelmedi cezası (${h.noShowSayisi} gün)`, tutar: -h.noShowCezasi })
  if (h.sabitEkOdeme > 0)
    k.push({ etiket: 'Sabit ek ödeme (yol/yemek)', tutar: h.sabitEkOdeme })
  if (h.sabitKesinti > 0)
    k.push({ etiket: 'Sabit kesinti (SGK/vergi)', tutar: -h.sabitKesinti })
  if (h.manuelEklemeToplam > 0)
    k.push({ etiket: 'Ek ödeme (manuel)', tutar: h.manuelEklemeToplam })
  if (h.manuelKesintiToplam > 0)
    k.push({ etiket: 'Kesinti (kasa eksiği vb.)', tutar: -h.manuelKesintiToplam })
  if (h.odenmisAvansToplam > 0)
    k.push({ etiket: 'Ödenen avans', tutar: -h.odenmisAvansToplam })

  return k
}

/** "2026-06-02" → "02.06" */
function tarihKisa(iso: string): string {
  const [, m, d] = iso.split('-')
  return d && m ? `${d}.${m}` : iso
}

/** FsTimestamp → "02.06" (puantaj tarihi için). */
function tsKisa(ts?: { toDate: () => Date } | null): string {
  if (!ts?.toDate) return ''
  const d = ts.toDate()
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`
}

/**
 * Ceza/uyarı çıkaran vardiyaları tarih + sebep + GERÇEK ceza tutarı ile döndürür.
 * Önemli: geç gelme tek başına para kesmez — ilk 2 geç cezasız, 3'üncüden itibaren ceza.
 * Erken çıkış: punitif ceza yoktur (eksik saat zaten ödemeye yansır). cezaTL=0 → "ceza yok".
 */
type VardiyaSorun = { tarih: string; tip: 'no_show' | 'gec' | 'erken'; dk: number; cezaTL: number }
function vardiyaSorunlari(hesap: {
  vardiyaKontrolSonuclari?: { vardiya: { tarih: string }; durum: string; gecmeDk: number; erkenCikmaDk: number }[]
  gecCezaBirimTL?: number
  noShowCezaBirimTL?: number
}): VardiyaSorun[] {
  const sonuclar = hesap.vardiyaKontrolSonuclari ?? []
  const gecBirim = hesap.gecCezaBirimTL ?? 0
  const noShowBirim = hesap.noShowCezaBirimTL ?? 0

  const noShowlar: VardiyaSorun[] = []
  const gecler: VardiyaSorun[] = []
  const erkenler: VardiyaSorun[] = []

  for (const s of sonuclar) {
    if (s.durum === 'no_show') {
      noShowlar.push({ tarih: s.vardiya.tarih, tip: 'no_show', dk: 0, cezaTL: noShowBirim })
    } else {
      if (s.gecmeDk > 0) gecler.push({ tarih: s.vardiya.tarih, tip: 'gec', dk: s.gecmeDk, cezaTL: 0 })
      if (s.erkenCikmaDk > 0) erkenler.push({ tarih: s.vardiya.tarih, tip: 'erken', dk: s.erkenCikmaDk, cezaTL: 0 })
    }
  }

  // Geç gelme: ilk 2 cezasız, 3'üncüden itibaren her biri gecBirim TL
  gecler.sort((a, b) => a.tarih.localeCompare(b.tarih))
  gecler.forEach((g, i) => { if (i >= 2) g.cezaTL = gecBirim })

  return [...noShowlar, ...gecler, ...erkenler].sort((a, b) => a.tarih.localeCompare(b.tarih))
}

export function MaaslarPage() {
  const { firebaseUser, kullanici } = useAuth()
  const duzenleyebilir = kullanici?.rol === 'boss'  // ödeme/ay kapanış BOSS'a özel
  const hareketYetkisi = useYetki([YETKILER.MAAS_HAREKET_EKLE])
  const hareketEkleyebilir = duzenleyebilir || hareketYetkisi  // kasa eksiği vb.
  const [donem, setDonem] = useState<string>(buAyStr())
  const [ozet, setOzet] = useState<ToplamMaliyetOzeti | null>(null)
  const [puantajMap, setPuantajMap] = useState<Map<string, Puantaj[]>>(new Map())
  const [detayAcikId, setDetayAcikId] = useState<string | null>(null)
  const [odemeler, setOdemeler] = useState<MaasOdemesi[]>([])
  const [aktifMaliyet, setAktifMaliyet] = useState<AktifMaliyetOzeti | null>(null)
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)
  const [subeler, setSubeler] = useState<Sube[]>([])
  const [pozisyonlar, setPozisyonlar] = useState<Pozisyon[]>([])
  const [ayDurum, setAyDurum] = useState<AyKapanisi | null>(null)
  const [kapanisModalAcik, setKapanisModalAcik] = useState(false)
  const [kapanisSebep, setKapanisSebep] = useState('')
  const [kapanisIslem, setKapanisIslem] = useState<'kapat' | 'ac'>('kapat')
  const [kapanisYukleniyor, setKapanisYukleniyor] = useState(false)

  const [odemeAcik, setOdemeAcik] = useState(false)
  const [odenecek, setOdenecek] = useState<{ p: KullaniciPersonel; net: number } | null>(null)
  const [iptalEdilecek, setIptalEdilecek] = useState<MaasOdemesi | null>(null)

  // YENİ — Maaş hareket modal state
  const [hareketModalAcik, setHareketModalAcik] = useState(false)
  const [hareketState, setHareketState] = useState<{ personel: KullaniciPersonel; tip: MaasHareketTipi } | null>(null)
  const [silinecekHareket, setSilinecekHareket] = useState<MaasHareketi | null>(null)

  const baseAnlikRef = useRef<Map<string, number>>(new Map())
  const baseTimestampRef = useRef<number>(Date.now())
  const [yansitilanSatirlar, setYansitilanSatirlar] = useState<Map<string, number>>(new Map())

  // Filtreler (MaliyetPage kalıbı)
  const [fSube, setFSube] = useState<string>('hepsi')
  const [fPozisyon, setFPozisyon] = useState<string>('hepsi')
  const [fDurum, setFDurum] = useState<'hepsi' | 'mesaide' | 'disi'>('hepsi')
  const [fMaasTipi, setFMaasTipi] = useState<'hepsi' | 'aylik' | 'saatlik'>('hepsi')

  const isBuAy = donem === buAyStr()

  const yukle = useCallback(async () => {
    try {
      setYukleniyor(true)
      const [o, od, am, subList, pozList, durum, puanlar] = await Promise.all([
        tumAktifPersonellerinMaliyeti(donem, { ayrilanlariDahilEt: true }),
        donemOdemelerini(donem),
        isBuAy ? aktifMesaiMaliyeti() : Promise.resolve(null),
        tumSubeleriGetir(),
        tumPozisyonlariGetir(),
        ayDurumu(donem),
        ayTumPuantajlar(donem),
      ])
      setOzet(o)
      setOdemeler(od)
      setAktifMaliyet(am)
      setSubeler(subList)
      setPozisyonlar(pozList)
      setAyDurum(durum)

      // Puantajları personel bazında grupla (iptal olmayanlar)
      const pMap = new Map<string, Puantaj[]>()
      aktifPuantajlar(puanlar).forEach((p) => {
        const arr = pMap.get(p.personelId) ?? []
        arr.push(p)
        pMap.set(p.personelId, arr)
      })
      setPuantajMap(pMap)

      const baseMap = new Map<string, number>()
      o.satirlar.forEach((s) => baseMap.set(s.personel.id, s.hesap.anlikNet))
      baseAnlikRef.current = baseMap
      baseTimestampRef.current = Date.now()
      setYansitilanSatirlar(new Map(baseMap))

      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Yüklenemedi: ' + (err as Error).message)
    } finally {
      setYukleniyor(false)
    }
  }, [donem, isBuAy])

  useEffect(() => { yukle() }, [yukle])

  useEffect(() => {
    if (!isBuAy) return
    const interval = setInterval(yukle, 30000)
    return () => clearInterval(interval)
  }, [isBuAy, yukle])

  useEffect(() => {
    if (!isBuAy || !aktifMaliyet || !ozet) return
    if (aktifMaliyet.aktifPersonelSayisi === 0) {
      setYansitilanSatirlar(new Map(baseAnlikRef.current))
      return
    }
    const interval = setInterval(() => {
      const gecenSn = (Date.now() - baseTimestampRef.current) / 1000
      const yeni = new Map(baseAnlikRef.current)
      aktifMaliyet.detay.forEach(({ personelId, saatlik }) => {
        const base = yeni.get(personelId) ?? 0
        const birikim = (saatlik / 3600) * gecenSn
        yeni.set(personelId, base + birikim)
      })
      setYansitilanSatirlar(yeni)
    }, 200)
    return () => clearInterval(interval)
  }, [aktifMaliyet, ozet, isBuAy])

  const odemeHaritasi = useMemo(() => {
    const m = new Map<string, MaasOdemesi[]>()
    odemeler
      .filter((o) => !o.iptal)
      .sort((a, b) => a.odemeTarihi.toMillis() - b.odemeTarihi.toMillis())
      .forEach((o) => {
        const list = m.get(o.personelId) ?? []
        list.push(o)
        m.set(o.personelId, list)
      })
    return m
  }, [odemeler])

  const aktifMesaidekiSet = useMemo(() => {
    const s = new Set<string>()
    aktifMaliyet?.detay.forEach((d) => s.add(d.personelId))
    return s
  }, [aktifMaliyet])

  // Filtrelenmiş personel satırları (liste + toplamlar bunu kullanır)
  const filtreliSatirlar = useMemo(() => {
    const satirlar = ozet?.satirlar ?? []
    return satirlar.filter(({ personel }) => {
      if (fSube !== 'hepsi' && !(personel.subeIdleri ?? []).includes(fSube)) return false
      if (fPozisyon !== 'hepsi' && !(personel.pozisyonIdleri ?? []).includes(fPozisyon)) return false
      if (fMaasTipi !== 'hepsi' && personel.maasTipi !== fMaasTipi) return false
      if (fDurum === 'mesaide' && !aktifMesaidekiSet.has(personel.id)) return false
      if (fDurum === 'disi' && aktifMesaidekiSet.has(personel.id)) return false
      return true
    })
  }, [ozet, fSube, fPozisyon, fMaasTipi, fDurum, aktifMesaidekiSet])

  const filtreVar =
    fSube !== 'hepsi' || fPozisyon !== 'hepsi' || fMaasTipi !== 'hepsi' || fDurum !== 'hepsi'

  function filtreleriTemizle() {
    setFSube('hepsi'); setFPozisyon('hepsi'); setFDurum('hepsi'); setFMaasTipi('hepsi')
  }

  const odenecekTutar = (personelId: string, hesap: { netMaas: number; anlikNet: number }) => {
    if (!isBuAy) return hesap.netMaas
    const aktifMi = aktifMesaidekiSet.has(personelId)
    if (aktifMi) return yansitilanSatirlar.get(personelId) ?? hesap.anlikNet
    return hesap.anlikNet
  }

  const toplamOdenen = (personelId: string): number => {
    const list = odemeHaritasi.get(personelId) ?? []
    return list.reduce((acc, o) => acc + o.netTutar, 0)
  }

  const odenenToplam = useMemo(
    () => odemeler.filter((o) => !o.iptal).reduce((acc, o) => acc + o.netTutar, 0),
    [odemeler]
  )

  const toplamOdenecek = useMemo(() => {
    return filtreliSatirlar.reduce(
      (acc, s) => acc + odenecekTutar(s.personel.id, s.hesap),
      0
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtreliSatirlar, isBuAy, yansitilanSatirlar, aktifMesaidekiSet])

  const bekleyenToplam = toplamOdenecek - odenenToplam

  const tamOdenenSayi = useMemo(() => {
    return filtreliSatirlar.filter(
      (s) => Math.abs(odenecekTutar(s.personel.id, s.hesap) - toplamOdenen(s.personel.id)) <= TOLERANS
        && toplamOdenen(s.personel.id) > 0
    ).length
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtreliSatirlar, odemeHaritasi, isBuAy, yansitilanSatirlar])

  const toplamPersonel = filtreliSatirlar.length

  async function ayKapanisIslem() {
    if (!firebaseUser) return
    setKapanisYukleniyor(true)
    try {
      if (kapanisIslem === 'kapat') {
        await ayKapat(donem, firebaseUser.uid, kapanisSebep)
      } else {
        await ayAc(donem, firebaseUser.uid, kapanisSebep)
      }
      setKapanisModalAcik(false)
      setKapanisSebep('')
      await yukle()
    } catch (err) {
      alert('İşlem başarısız: ' + (err as Error).message)
    } finally {
      setKapanisYukleniyor(false)
    }
  }

  function indir() {
    if (!ozet) return
    // Ekranda filtre aktifse CSV de filtreli iner (şube bazlı export).
    const csvOzet = filtreVar ? { ...ozet, satirlar: filtreliSatirlar } : ozet
    const csv = maaslariCsvOlustur({
      donem, ozet: csvOzet, odemeler, subeler, pozisyonlar, isBuAy,
    })
    csvIndir(csv, `maaslar-${donem}.csv`)
  }

  function ayDegistir(yon: -1 | 1) {
    const [y, m] = donem.split('-').map(Number)
    if (!y || !m) return
    const d = new Date(y, m - 1 + yon, 1)
    setDonem(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }

  async function iptalOnay() {
    if (!iptalEdilecek || !firebaseUser) return
    try {
      await odemeIptalEt(iptalEdilecek.id, firebaseUser.uid)
      setIptalEdilecek(null)
      await yukle()
    } catch (err) {
      alert('İptal edilemedi: ' + (err as Error).message)
    }
  }

  async function hareketSilOnay() {
    if (!silinecekHareket) return
    try {
      await maasHareketiSil(silinecekHareket.id)
      setSilinecekHareket(null)
      await yukle()
    } catch (err) {
      alert('Silinemedi: ' + (err as Error).message)
    }
  }

  function hareketEkle(personel: KullaniciPersonel, tip: MaasHareketTipi) {
    setHareketState({ personel, tip })
    setHareketModalAcik(true)
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Coins className="w-6 h-6 text-amber-600" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Maaşlar</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            {isBuAy ? 'Anlık biriken net (devam eden ay)' : 'Ay sonu kesinleşmiş net'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {duzenleyebilir && (
            <button
              onClick={() => {
                setKapanisIslem(ayDurum?.kapali ? 'ac' : 'kapat')
                setKapanisSebep('')
                setKapanisModalAcik(true)
              }}
              disabled={!ayDurum}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${
                ayDurum?.kapali
                  ? 'bg-amber-600 hover:bg-amber-700 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              {ayDurum?.kapali ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              <span className="hidden sm:inline">{ayDurum?.kapali ? 'Ayı Aç' : 'Ayı Kapat'}</span>
            </button>
          )}
          <button
            onClick={indir}
            disabled={!ozet || ozet.satirlar.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white rounded-xl text-sm font-semibold transition-colors"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">CSV İndir</span>
          </button>
        </div>
      </div>

      {ayDurum?.kapali && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-start gap-2">
          <Lock className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-amber-900">
            <p className="font-semibold">Bu ay kapatıldı (finalize edildi).</p>
            {ayDurum.sonIslem && (
              <p className="text-xs mt-0.5">
                {ayDurum.sonIslem.tarih.toDate().toLocaleString('tr-TR')}
                {ayDurum.sonIslem.sebep && ` — ${ayDurum.sonIslem.sebep}`}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="card mb-4">
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => ayDegistir(-1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 text-center font-semibold text-gray-900 flex items-center justify-center gap-2">
            {ayEtiketi(donem)}
            {isBuAy && (
              <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full inline-flex items-center gap-0.5">
                <Activity className="w-2.5 h-2.5 animate-pulse" />
                canlı
              </span>
            )}
          </div>
          <button onClick={() => ayDegistir(1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <ChevronRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => setDonem(buAyStr())}
            className="px-3 py-1.5 text-xs text-brand-600 hover:underline whitespace-nowrap"
          >
            Bu ay
          </button>
        </div>

        {ozet && (
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-blue-50 rounded-lg p-2">
              <div className="text-xs text-blue-700">
                {isBuAy ? 'Şu Ana Kadar' : 'Toplam Net'}
              </div>
              <div className="font-bold text-blue-900 tabular-nums">
                {isBuAy && (aktifMaliyet?.aktifPersonelSayisi ?? 0) > 0
                  ? paraKurus(toplamOdenecek)
                  : paraTam(toplamOdenecek)} ₺
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-2">
              <div className="text-xs text-green-700">Ödenen</div>
              <div className="font-bold text-green-900">
                {paraTam(odenenToplam)} ₺
                <div className="text-[10px] font-normal">
                  {tamOdenenSayi} / {toplamPersonel}
                </div>
              </div>
            </div>
            <div className={`${bekleyenToplam < -TOLERANS ? 'bg-red-50' : 'bg-amber-50'} rounded-lg p-2`}>
              <div className={`text-xs ${bekleyenToplam < -TOLERANS ? 'text-red-700' : 'text-amber-700'}`}>
                {bekleyenToplam < -TOLERANS ? 'Fazla Ödeme' : 'Bekleyen'}
              </div>
              <div className={`font-bold ${bekleyenToplam < -TOLERANS ? 'text-red-900' : 'text-amber-900'} tabular-nums`}>
                {isBuAy && (aktifMaliyet?.aktifPersonelSayisi ?? 0) > 0
                  ? paraKurus(Math.abs(bekleyenToplam))
                  : paraTam(Math.abs(bekleyenToplam))} ₺
              </div>
            </div>
          </div>
        )}
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm mb-4">
          {hata}
        </div>
      )}

      {!yukleniyor && ozet && ozet.satirlar.length > 0 && (
        <div className="card mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-gray-500" />
            <h2 className="font-bold text-sm text-gray-900">Filtrele</h2>
            {filtreVar && (
              <button
                onClick={filtreleriTemizle}
                className="ml-auto text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Temizle
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={fSube}
              onChange={(e) => setFSube(e.target.value)}
              className="input-base text-sm"
            >
              <option value="hepsi">Tüm şubeler</option>
              {subeler.map((s) => (
                <option key={s.id} value={s.id}>{s.isim}</option>
              ))}
            </select>
            <select
              value={fPozisyon}
              onChange={(e) => setFPozisyon(e.target.value)}
              className="input-base text-sm"
            >
              <option value="hepsi">Tüm pozisyonlar</option>
              {pozisyonlar.map((p) => (
                <option key={p.id} value={p.id}>{p.isim}</option>
              ))}
            </select>
            <select
              value={fDurum}
              onChange={(e) => setFDurum(e.target.value as typeof fDurum)}
              className="input-base text-sm"
            >
              <option value="hepsi">Tüm durumlar</option>
              <option value="mesaide">Sadece mesaide</option>
              <option value="disi">Sadece mesai dışı</option>
            </select>
            <select
              value={fMaasTipi}
              onChange={(e) => setFMaasTipi(e.target.value as typeof fMaasTipi)}
              className="input-base text-sm"
            >
              <option value="hepsi">Tüm maaş tipleri</option>
              <option value="aylik">Aylık</option>
              <option value="saatlik">Saatlik</option>
            </select>
          </div>
        </div>
      )}

      {yukleniyor && !ozet ? (
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : !ozet || ozet.satirlar.length === 0 ? (
        <div className="card text-center py-12">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="font-semibold text-gray-700">Aktif personel yok</p>
        </div>
      ) : filtreliSatirlar.length === 0 ? (
        <div className="card text-center py-12">
          <Filter className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="font-semibold text-gray-700">Filtreye uyan personel yok</p>
          <button onClick={filtreleriTemizle} className="mt-2 text-sm text-brand-600 hover:underline">
            Filtreyi temizle
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtreliSatirlar.map(({ personel, hesap }) => {
            const odemeListesi = odemeHaritasi.get(personel.id) ?? []
            const odenenTop = toplamOdenen(personel.id)
            const tutar = odenecekTutar(personel.id, hesap)
            const fark = tutar - odenenTop

            const tamOdendi = odenenTop > 0 && Math.abs(fark) <= TOLERANS
            const fazlaOdeme = fark < -TOLERANS
            const kismiOdendi = odenenTop > 0 && fark > TOLERANS
            const hicOdenmedi = odenenTop === 0
            const eksiBakiye = tutar < 0  // Anlık net negatif

            const aktifMi = aktifMesaidekiSet.has(personel.id) && isBuAy
            const canliMi = aktifMi && !tamOdendi

            const kartRengi =
              eksiBakiye ? 'ring-2 ring-red-500 bg-red-100'
              : fazlaOdeme ? 'bg-red-50 border-red-200'
              : tamOdendi ? 'bg-green-50 border-green-200'
              : kismiOdendi ? 'bg-amber-50 border-amber-200'
              : ''

            return (
              <div key={personel.id} className={`card ${kartRengi}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-900 truncate">{personel.isim}</h3>

                      {eksiBakiye && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-red-600 text-white rounded-full inline-flex items-center gap-0.5 font-bold">
                          <AlertCircle className="w-2.5 h-2.5" />
                          Eksi Bakiye
                        </span>
                      )}
                      {tamOdendi && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full inline-flex items-center gap-0.5">
                          <CheckCircle2 className="w-2.5 h-2.5" />
                          Tamamı Ödendi
                        </span>
                      )}
                      {kismiOdendi && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-full">
                          Kısmi Ödendi
                        </span>
                      )}
                      {fazlaOdeme && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full inline-flex items-center gap-0.5">
                          <AlertCircle className="w-2.5 h-2.5" />
                          Fazla Ödeme
                        </span>
                      )}
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                        {personel.maasTipi === 'aylik' ? 'Aylık' : 'Saatlik'}
                      </span>
                      {!personel.aktif && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full font-semibold">
                          Ayrıldı
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-600 flex-wrap">
                      <span>{hesap.toplamSaat.toFixed(1)} saat</span>
                      {hesap.puantajToplam !== 0 && (
                        <span className={hesap.puantajToplam > 0 ? 'text-green-700' : 'text-red-700'}>
                          P: {hesap.puantajToplam > 0 ? '+' : ''}{paraTam(hesap.puantajToplam)} ₺
                        </span>
                      )}
                      {hesap.odenmisAvansToplam > 0 && (
                        <span className="text-amber-700">
                          A: −{paraTam(hesap.odenmisAvansToplam)} ₺
                        </span>
                      )}
                      {hesap.manuelEklemeToplam > 0 && (
                        <span className="text-green-700">
                          +{paraTam(hesap.manuelEklemeToplam)} ₺ ekleme
                        </span>
                      )}
                      {hesap.manuelKesintiToplam > 0 && (
                        <span className="text-red-700">
                          −{paraTam(hesap.manuelKesintiToplam)} ₺ kesinti
                        </span>
                      )}
                    </div>

                    {/* YENİ — Ceza/uyarı tarih + sebep + ceza durumu (şeffaflık) */}
                    {(() => {
                      const sorunlar = vardiyaSorunlari(hesap)
                      if (sorunlar.length === 0) return null
                      return (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {sorunlar.map((s, i) => {
                            const cezali = s.tip === 'no_show' || s.cezaTL > 0
                            const olay =
                              s.tip === 'no_show' ? `Gelmedi · ${tarihKisa(s.tarih)}`
                              : s.tip === 'gec' ? `Geç · ${tarihKisa(s.tarih)} +${s.dk}dk`
                              : `Erken · ${tarihKisa(s.tarih)} −${s.dk}dk`
                            const cezaMetni =
                              s.tip === 'no_show'
                                ? (s.cezaTL > 0 ? `o gün ödenmez + ceza −${paraTam(s.cezaTL)} ₺` : 'o gün ödenmez')
                                : (cezali ? `ceza −${paraTam(s.cezaTL)} ₺` : 'ceza yok')
                            const ipucu =
                              s.tip === 'no_show'
                                ? `${tarihKisa(s.tarih)}: O gün giriş kaydı yok. Sözlü değişiklik/izin varsa o günün vardiyasını "izinli" yapın.`
                                : s.tip === 'gec'
                                  ? (cezali
                                      ? `${tarihKisa(s.tarih)}: 3'üncü+ geç gelme → ${paraTam(s.cezaTL)} ₺ ceza`
                                      : `${tarihKisa(s.tarih)}: Geç giriş kayıtlı. İlk 2 geç gelmede para cezası YOKTUR (ceza 3'üncüden başlar).`)
                                  : `${tarihKisa(s.tarih)}: Erken çıkış kayıtlı. Punitif ceza yoktur (eksik saat zaten ödemeye yansır).`
                            return (
                              <span
                                key={i}
                                className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold inline-flex items-center gap-1 ${
                                  cezali
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-100 text-gray-500'
                                }`}
                                title={ipucu}
                              >
                                <span>{cezali ? '🔴' : 'ⓘ'}</span>
                                <span>{olay}</span>
                                <span className={cezali ? 'opacity-90' : 'opacity-80'}>· {cezaMetni}</span>
                              </span>
                            )
                          })}
                        </div>
                      )
                    })()}

                    {/* YENİ — Puantaj kalemleri (tutar + açıklama + tarih) */}
                    {(() => {
                      const liste = puantajMap.get(personel.id) ?? []
                      if (liste.length === 0) return null
                      return (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {liste.map((p) => {
                            const art = p.tutar > 0
                            return (
                              <span
                                key={p.id}
                                className={`text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 ${
                                  art ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}
                                title={`${tsKisa(p.tarih)} · ${p.aciklama}`}
                              >
                                <span className="font-semibold">
                                  {art ? '+' : '−'}{paraTam(Math.abs(p.tutar))} ₺
                                </span>
                                <span className="opacity-90">· {p.aciklama}</span>
                                <span className="opacity-70">· {tsKisa(p.tarih)}</span>
                              </span>
                            )
                          })}
                        </div>
                      )
                    })()}

                    {/* YENİ — Maaş detayı (kalem kalem net) */}
                    <div className="mt-2">
                      <button
                        onClick={() => setDetayAcikId(detayAcikId === personel.id ? null : personel.id)}
                        className="text-xs px-2 py-1 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 inline-flex items-center gap-1"
                      >
                        {detayAcikId === personel.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        Detay
                      </button>

                      {detayAcikId === personel.id && (
                        <div className="mt-2 border border-gray-200 rounded-xl p-3 bg-gray-50 space-y-1">
                          {maasKalemleri(hesap, personel).map((kalem, idx) => (
                            <div key={idx} className="flex items-baseline justify-between gap-2 text-xs">
                              <div className="min-w-0">
                                <span className="text-gray-700">{kalem.etiket}</span>
                                {kalem.alt && (
                                  <span className="block text-[10px] text-gray-400">{kalem.alt}</span>
                                )}
                              </div>
                              <span className={`tabular-nums font-semibold flex-shrink-0 ${
                                kalem.tutar < 0 ? 'text-red-600' : 'text-gray-800'
                              }`}>
                                {kalem.tutar < 0 ? '−' : '+'}{paraTam(Math.abs(kalem.tutar))} ₺
                              </span>
                            </div>
                          ))}
                          <div className="flex items-baseline justify-between gap-2 pt-2 mt-1 border-t border-gray-300">
                            <span className="text-sm font-bold text-gray-900">Net</span>
                            <span className="text-sm font-bold text-gray-900 tabular-nums">
                              {paraTam(hesap.netMaas)} ₺
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {isBuAy && !tamOdendi && (
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        ay sonu tahmini: {paraTam(hesap.netMaas)} ₺
                      </p>
                    )}

                    {/* Manuel hareketler listesi */}
                    {hesap.manuelHareketler.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {hesap.manuelHareketler.map((h) => {
                          const ekleme = h.tip === 'ekleme'
                          const bekliyor = h.durum === 'bekliyor'
                          return (
                            <div
                              key={h.id}
                              className={`flex items-center gap-2 text-xs rounded-lg px-2 py-1.5 ${
                                bekliyor
                                  ? 'bg-amber-50 border border-amber-300'
                                  : ekleme
                                    ? 'bg-green-50 border border-green-200'
                                    : 'bg-red-50 border border-red-200'
                              }`}
                            >
                              {ekleme ? (
                                <Plus className="w-3 h-3 text-green-700 flex-shrink-0" />
                              ) : (
                                <Minus className="w-3 h-3 text-red-700 flex-shrink-0" />
                              )}
                              <span className={`font-semibold ${ekleme ? 'text-green-900' : 'text-red-900'}`}>
                                {ekleme ? '+' : '−'}{paraTam(h.tutar)} ₺
                              </span>
                              {bekliyor && (
                                <span className="text-[9px] px-1 py-0.5 bg-amber-200 text-amber-800 rounded font-bold uppercase flex-shrink-0">
                                  Onay bekliyor
                                </span>
                              )}
                              <span className="text-gray-700 truncate flex-1">
                                {h.aciklama}
                              </span>
                              <span className="text-[10px] text-gray-500 flex-shrink-0">
                                {h.olusturanRol === 'boss' ? 'BOSS' : 'Yönetici'}
                              </span>
                              {duzenleyebilir && (
                                <button
                                  onClick={() => setSilinecekHareket(h)}
                                  className="text-gray-400 hover:text-red-700 flex-shrink-0"
                                  title="Sil"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {/* Ödeme listesi */}
                    {odemeListesi.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {odemeListesi.map((odeme) => (
                          <div key={odeme.id} className="flex items-center gap-2 text-xs bg-white rounded-lg px-2 py-1.5">
                            {odeme.odemeTipi === 'nakit' && (
                              <><Banknote className="w-3 h-3 text-green-700" />
                                <span className="text-gray-800">Nakit {paraTam(odeme.netTutar)} ₺</span></>
                            )}
                            {odeme.odemeTipi === 'iban' && (
                              <><Building className="w-3 h-3 text-blue-700" />
                                <span className="text-gray-800">IBAN {paraTam(odeme.netTutar)} ₺</span></>
                            )}
                            {odeme.odemeTipi === 'karma' && (
                              <><GitMerge className="w-3 h-3 text-purple-700" />
                                <span className="text-gray-800">
                                  Karma: N{paraTam(odeme.nakitTutar ?? 0)} + I{paraTam(odeme.ibanTutar ?? 0)} ₺
                                </span></>
                            )}
                            <span className="ml-auto text-[10px] text-gray-500">
                              {odeme.odemeTarihi.toDate().toLocaleDateString('tr-TR')}
                            </span>
                            {duzenleyebilir && (
                              <button
                                onClick={() => setIptalEdilecek(odeme)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        ))}
                        {kismiOdendi && (
                          <div className="flex items-center justify-between text-xs bg-amber-100 border border-amber-200 rounded-lg px-2 py-1.5">
                            <span className="text-amber-900 font-semibold">Kalan</span>
                            <span className="font-bold text-amber-900 tabular-nums">
                              +{canliMi ? paraKurus(fark) : paraTam(fark)} ₺
                            </span>
                          </div>
                        )}
                        {fazlaOdeme && (
                          <div className="flex items-center justify-between text-xs bg-red-100 border border-red-300 rounded-lg px-2 py-1.5">
                            <span className="text-red-900 font-semibold flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> Fazla
                            </span>
                            <span className="font-bold text-red-900 tabular-nums">
                              {paraTam(fark)} ₺
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* YENİ — Ekleme/Kesinti butonları */}
                    {hareketEkleyebilir && (
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => hareketEkle(personel, 'ekleme')}
                          className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold inline-flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" />
                          Ekleme
                        </button>
                        <button
                          onClick={() => hareketEkle(personel, 'kesinti')}
                          className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold inline-flex items-center gap-1"
                        >
                          <Minus className="w-3 h-3" />
                          Kesinti
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="text-right flex-shrink-0">
                    <p className={`text-xl font-bold tabular-nums ${
                      eksiBakiye ? 'text-red-700' : 'text-gray-900'
                    }`}>
                      {canliMi ? paraKurus(tutar) : paraTam(tutar)}
                    </p>
                    <p className="text-xs text-gray-500 mb-2">
                      ₺ {isBuAy ? 'anlık net' : 'net'}
                    </p>

                    {hicOdenmedi && !eksiBakiye && duzenleyebilir && (
                      <button
                        onClick={() => {
                          setOdenecek({ p: personel, net: tutar })
                          setOdemeAcik(true)
                        }}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Öde
                      </button>
                    )}

                    {kismiOdendi && duzenleyebilir && (
                      <button
                        onClick={() => {
                          setOdenecek({ p: personel, net: fark })
                          setOdemeAcik(true)
                        }}
                        className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" />
                        Ek Öde {paraTam(fark)} ₺
                      </button>
                    )}

                    {tamOdendi && (
                      <span className="text-[10px] text-green-700 font-semibold inline-flex items-center gap-0.5">
                        <CheckCircle2 className="w-3 h-3" />
                        kapatıldı
                      </span>
                    )}

                    {fazlaOdeme && (
                      <p className="text-[10px] text-red-700">İade gerek</p>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <OdemeModal
        acik={odemeAcik}
        personel={odenecek?.p ?? null}
        donem={donem}
        netTutar={odenecek?.net ?? 0}
        onKapat={() => setOdemeAcik(false)}
        onKaydedildi={() => {
          setOdemeAcik(false)
          yukle()
        }}
      />

      <MaasHareketEkleModal
        acik={hareketModalAcik}
        personel={hareketState?.personel ?? null}
        donem={donem}
        baslangicTipi={hareketState?.tip ?? 'kesinti'}
        onKapat={() => setHareketModalAcik(false)}
        onKaydedildi={() => {
          setHareketModalAcik(false)
          yukle()
        }}
      />

      <ConfirmDialog
        acik={!!iptalEdilecek}
        baslik="Ödemeyi İptal Et"
        mesaj="Bu ödeme kaydı iptal edilecek. Devam mı?"
        onayMetni="İptal Et"
        tehlikeli
        onOnay={iptalOnay}
        onIptal={() => setIptalEdilecek(null)}
      />

      <ConfirmDialog
        acik={!!silinecekHareket}
        baslik="Hareketi Sil"
        mesaj={silinecekHareket
          ? `"${silinecekHareket.aciklama}" — ${silinecekHareket.tip === 'ekleme' ? '+' : '−'}${paraTam(silinecekHareket.tutar)} ₺ silinecek. Devam mı?`
          : ''}
        onayMetni="Sil"
        tehlikeli
        onOnay={hareketSilOnay}
        onIptal={() => setSilinecekHareket(null)}
      />

      {kapanisModalAcik && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !kapanisYukleniyor && setKapanisModalAcik(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              {kapanisIslem === 'kapat' ? <Lock className="w-5 h-5 text-amber-600" /> : <Unlock className="w-5 h-5 text-amber-600" />}
              <h3 className="text-lg font-bold text-gray-900">
                {kapanisIslem === 'kapat' ? 'Ayı Kapat' : 'Ayı Aç'}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {kapanisIslem === 'kapat' ? (
                <><strong>{donem}</strong> ayı kapatılacak (finalize).</>
              ) : (
                <><strong>{donem}</strong> ayı tekrar açılacak.</>
              )}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Sebep (opsiyonel)
              </label>
              <input
                type="text"
                value={kapanisSebep}
                onChange={(e) => setKapanisSebep(e.target.value)}
                className="input-base"
                disabled={kapanisYukleniyor}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setKapanisModalAcik(false)}
                disabled={kapanisYukleniyor}
                className="btn-secondary flex-1"
              >
                İptal
              </button>
              <button
                onClick={ayKapanisIslem}
                disabled={kapanisYukleniyor}
                className={`flex-1 px-4 py-2 rounded-xl text-white font-semibold ${
                  kapanisIslem === 'kapat' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {kapanisYukleniyor ? 'İşleniyor...' : (kapanisIslem === 'kapat' ? 'Kapat' : 'Aç')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
